import { useLazyQuery } from '@apollo/client'
import { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { useAuth } from '../../../context/auth-context'
import { ACTIVE_EMPLOYEE_SEARCH } from '../../../graphql/employee/Queries'
import { ActiveEmployeesSearchQuery, ActiveEmployeesSearchQueryVariables } from '../../../types'
import { sortFullNameAndCompanyBySearchTerm } from './utils'

export const EMPLOYEE_SEARCH_LIMIT = 5

export const useActiveEmployeeSearch = (
  kudosView: boolean,
  companyId?: string,
): {
  searchTerm: string
  setSearchTerm: (value: string) => void
  handleSearch: (value: string) => void
  handleClear: () => void
  employees: ActiveEmployeesSearchQuery['activeEmployeesSearch']
  loading: boolean
  shouldShowOptions: boolean
} => {
  const { user } = useAuth()
  const locale = user.locale.replace('_', '-')
  const [searchTerm, setSearchTerm] = useState('')
  const [employees, setEmployees] = useState<ActiveEmployeesSearchQuery['activeEmployeesSearch']>([])
  const [isSearching, setIsSearching] = useState(false)
  const timer = useRef<number>()

  const [getEmployees, { data, loading }] = useLazyQuery<ActiveEmployeesSearchQuery, ActiveEmployeesSearchQueryVariables>(
    ACTIVE_EMPLOYEE_SEARCH,
    {
      variables: { search: searchTerm, limit: EMPLOYEE_SEARCH_LIMIT, kudosView, companyId },
      fetchPolicy: 'network-only',
    },
  )

  useEffect(() => {
    if (data?.activeEmployeesSearch && !loading) {
      setEmployees(data.activeEmployeesSearch)
      setIsSearching(false)
    }
  }, [data, loading])

  useEffect(() => {
    setEmployees([])
  }, [searchTerm])

  const handleSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim()
      setSearchTerm(trimmedValue)

      // Clear existing timer
      if (timer.current) {
        clearTimeout(timer.current)
      }

      // Set loading state when starting search
      if (trimmedValue.length > 2) {
        setIsSearching(true)
      }

      // Set new timer for debounced search
      timer.current = window.setTimeout(() => {
        // Only search if there's at least one keyword with 3+ characters
        const atLeastOneKeywordWithThreeOrMoreLetters = trimmedValue.split(' ').some((keyword) => keyword.length > 2)

        if (atLeastOneKeywordWithThreeOrMoreLetters) {
          getEmployees({ variables: { search: trimmedValue, limit: EMPLOYEE_SEARCH_LIMIT, kudosView, companyId } })
        } else {
          setIsSearching(false)
        }
      }, 1000)
    },
    [getEmployees, kudosView, companyId],
  )

  const handleClear = useCallback(() => {
    setSearchTerm('')
    setEmployees([])
    setIsSearching(false)
    if (timer.current) {
      clearTimeout(timer.current)
    }
  }, [])

  const sortedEmployees = useMemo(
    () => [...employees].sort((a, b) => sortFullNameAndCompanyBySearchTerm(a, b, searchTerm, locale)),
    [employees, locale, searchTerm],
  )

  const shouldShowOptions = useMemo(() => {
    return searchTerm.length > 2 && sortedEmployees.length > 0
  }, [searchTerm, sortedEmployees])

  return {
    searchTerm,
    setSearchTerm,
    handleSearch,
    handleClear,
    employees: sortedEmployees,
    loading: loading || isSearching,
    shouldShowOptions,
  }
}
